import PptxGenJS from "pptxgenjs";


import { toPptImageSource } from "./pptImageSource";
import { pptTheme as defaultPptTheme } from "./theme";
import {
  TEACHER_MANUAL_LAYOUT,
  addTeacherManualSlide,
  isTeacherManualSlide,
} from "./renderTeacherManualSlide";

const MARGIN_X = 0.6;
const CONTENT_W = 8.8;

function resolveDocumentTheme(document) {
  return document.theme ?? defaultPptTheme;
}

function addSlideImage(slide, imageUrl, layout) {
  const imageSource = toPptImageSource(imageUrl);
  if (!imageSource) {
    return;
  }

  if (layout === "title") {
    slide.addImage({
      ...imageSource,
      x: 6.2,
      y: 1.4,
      w: 3.0,
      h: 3.8,
    });
    return;
  }

  if (layout === "section") {
    slide.addImage({
      ...imageSource,
      x: 6.0,
      y: 1.0,
      w: 3.2,
      h: 4.5,
    });
    return;
  }

  slide.addImage({
    ...imageSource,
    x: 6.0,
    y: 0.8,
    w: 3.2,
    h: 4.8,
  });
}

function addTitleSlide(pptx, slide, theme) {
  const s = pptx.addSlide();
  s.addShape(pptx.ShapeType.rect, {
    x: 0,
    y: 0,
    w: "100%",
    h: 1.1,
    fill: { color: theme.titleBar ?? theme.primary },
  });

  const hasImage = Boolean(slide.imageUrl);
  const titleWidth = hasImage ? 5.2 : CONTENT_W;

  s.addText(slide.title, {
    x: MARGIN_X,
    y: 1.6,
    w: titleWidth,
    h: 1.4,
    fontSize: 28,
    bold: true,
    color: theme.text,
    fontFace: "Calibri"
  });
  if (slide.subtitle) {
    s.addText(slide.subtitle, {
      x: MARGIN_X,
      y: 3.1,
      w: titleWidth,
      h: 1.2,
      fontSize: 14,
      color: theme.muted,
      fontFace: "Calibri"
    });
  }
  if (hasImage) {
    addSlideImage(s, slide.imageUrl, "title");
  }
}

function addSectionSlide(pptx, slide, theme) {
  const s = pptx.addSlide();
  const hasImage = Boolean(slide.imageUrl);
  const contentWidth = hasImage ? 5.0 : CONTENT_W;

  s.addText(slide.title, {
    x: MARGIN_X,
    y: 1.2,
    w: contentWidth,
    h: 0.8,
    fontSize: 22,
    bold: true,
    color: theme.primary,
    fontFace: "Calibri"
  });
  if (slide.lead) {
    s.addText(slide.lead, {
      x: MARGIN_X,
      y: 2.1,
      w: contentWidth,
      h: 0.6,
      fontSize: 14,
      bold: true,
      color: theme.text,
      fontFace: "Calibri",
      valign: "top",
    });
  }
  if (slide.body) {
    s.addText(slide.body, {
      x: MARGIN_X,
      y: slide.lead ? 2.8 : 2.1,
      w: contentWidth,
      h: slide.lead ? 3.8 : 4.5,
      fontSize: 14,
      color: theme.text,
      fontFace: "Calibri",
      valign: "top"
    });
  }
  if (hasImage) {
    addSlideImage(s, slide.imageUrl, "section");
  }
}

function addBulletsSlide(pptx, slide, theme) {
  const s = pptx.addSlide();
  let contentY = 0.5;
  const hasImage = Boolean(slide.imageUrl);
  const contentWidth = hasImage ? 5.0 : CONTENT_W;

  s.addText(slide.title, {
    x: MARGIN_X,
    y: contentY,
    w: contentWidth,
    h: 0.7,
    fontSize: 18,
    bold: true,
    color: theme.text,
    fontFace: "Calibri"
  });
  contentY += 0.85;

  if (slide.body) {
    s.addText(slide.body, {
      x: MARGIN_X,
      y: contentY,
      w: contentWidth,
      h: 1.2,
      fontSize: 14,
      color: theme.text,
      fontFace: "Calibri",
      valign: "top"
    });
    contentY += 1.35;
  }

  const bullets = slide.bullets ?? [];
  if (bullets.length > 0) {
    s.addText(bullets.join("\n"), {
      x: MARGIN_X,
      y: contentY,
      w: contentWidth,
      h: slide.subtitle ? 4.2 : 5,
      fontSize: 13,
      color: theme.text,
      fontFace: "Calibri",
      bullet: true,
      valign: "top"
    });
  }

  if (hasImage) {
    addSlideImage(s, slide.imageUrl, "bullets");
  }

  if (slide.subtitle) {
    s.addText(slide.subtitle, {
      x: MARGIN_X,
      y: 6.2,
      w: contentWidth,
      h: 0.6,
      fontSize: 11,
      italic: true,
      color: theme.muted,
      fontFace: "Calibri"
    });
  }
}

function addTableSlide(pptx, slide, theme) {
  const bullets = [];
  if (slide.headers?.length) {
    bullets.push(slide.headers.join(" | "));
  }
  for (const row of slide.rows ?? []) {
    bullets.push(row.join(" | "));
  }
  addBulletsSlide(pptx, {
    kind: "bullets",
    title: slide.title,
    body: slide.body,
    bullets,
  }, theme);
}

function addSlide(pptx, slide, theme, index) {
  if (isTeacherManualSlide(slide)) {
    addTeacherManualSlide(pptx, slide, index);
    return;
  }

  switch (slide.kind) {
    case "title":
      addTitleSlide(pptx, slide, theme);
      break;
    case "section":
      addSectionSlide(pptx, slide, theme);
      break;
    case "bullets":
      addBulletsSlide(pptx, slide, theme);
      break;
    case "table":
      addTableSlide(pptx, slide, theme);
      break;
  }
}

export function renderPptDocument(document) {
  const pptx = new PptxGenJS();
  const theme = resolveDocumentTheme(document);
  const usesTeacherManual = document.template === "teacher-manual"
    || (document.slides ?? []).some(isTeacherManualSlide);

  if (usesTeacherManual) {
    pptx.defineLayout({
      name: TEACHER_MANUAL_LAYOUT.name,
      width: TEACHER_MANUAL_LAYOUT.width,
      height: TEACHER_MANUAL_LAYOUT.height,
    });
    pptx.layout = TEACHER_MANUAL_LAYOUT.name;
  } else {
    pptx.layout = "LAYOUT_16x9";
  }

  pptx.author = document.author ?? "Tema";
  pptx.title = document.title;
  if (document.subject) {
    pptx.subject = document.subject;
  }

  (document.slides ?? []).forEach((slide, index) => {
    addSlide(pptx, slide, theme, index);
  });

  return pptx;
}